import { matchPath } from 'react-router-dom';
import type { UserRole } from '@/api/models';
import { PATH } from '@/router/PATH';

export type SecuredNavigationItem = {
    badge?: string;
    href: string;
    icon: string;
    labelKey: string;
    matchPaths?: string[];
    /** When set, the item is only shown to callers whose role is listed. */
    roles?: UserRole[];
    slug: string;
};

export type SecuredNavigationGroup = {
    icon: string;
    items: SecuredNavigationItem[];
    labelKey: string;
    slug: string;
};

export const SECURED_NAVIGATION_ITEMS: SecuredNavigationItem[] = [
    // First row, and the app's landing page. This is the dashboard BOS-170 said
    // would "come back when there is a dashboard behind it": until it existed,
    // `/` redirected to `/sales` and a "Dashboard" row would have been a second
    // label for Sales — it would have sent you there, and Sales would have
    // highlighted. Now `/` renders this screen, so the row highlights itself.
    {
        href: PATH.HOME,
        // Both of its addresses. `/` is where the app lands; the named path is
        // what an in-app link to this screen says. Neither redirects to the
        // other, so the item has to claim both or one of them highlights
        // nothing.
        matchPaths: [PATH.HOME, PATH.CONTROL_CENTER],
        icon: 'solar:widget-5-bold-duotone',
        labelKey: 'layout.navigation.controlCenter',
        slug: 'control-center',
    },
    {
        href: PATH.SALES,
        // `/` belongs to the Control Center now, not to Sales — Sales held it
        // only while it was the redirect target of a home page that had no
        // screen of its own.
        matchPaths: [PATH.SALES],
        icon: 'solar:chart-square-bold-duotone',
        labelKey: 'layout.navigation.sales',
        slug: 'sales',
    },
    // No Shifts item: the shift register is a tab of Branches now, and
    // `/branches` matches it by prefix. A second row pointing into an area that
    // already has one is the Dashboard mistake again — it would highlight
    // Branches, not itself. Its role gate moved with it, into
    // `branchesTabsMeta`; there is no sidebar row left to carry it.
    {
        href: PATH.ANALYTICS.ROOT,
        icon: 'solar:pie-chart-2-bold-duotone',
        labelKey: 'layout.navigation.analytics',
        // Prefix match, like the Accounting shells: the tab routes live under
        // this path and are the same nav destination, so every one of them
        // should highlight Analytics and title the navbar "Analytics".
        matchPaths: [PATH.ANALYTICS.ROOT],
        // A cross-business reporting surface, same gate as the ledger screens.
        roles: ['OrgAdmin', 'BranchAdmin'],
        slug: 'analytics',
    },
    {
        href: PATH.PURCHASES.ROOT,
        icon: 'solar:cart-large-2-bold-duotone',
        labelKey: 'layout.navigation.purchases',
        matchPaths: [PATH.PURCHASES.ROOT],
        slug: 'purchases',
    },
    {
        href: PATH.TEAM,
        icon: 'solar:users-group-rounded-bold-duotone',
        labelKey: 'layout.navigation.team',
        matchPaths: [PATH.TEAM],
        // Management surface — sellers never see it.
        roles: ['OrgAdmin', 'BranchAdmin'],
        slug: 'team',
    },
];

export const SECURED_NAVIGATION_GROUPS: SecuredNavigationGroup[] = [
    {
        icon: 'solar:folder-with-files-bold-duotone',
        labelKey: 'layout.navigation.groups.branchesAndProducts',
        slug: 'branches-and-products',
        items: [
            {
                href: PATH.BRANCHES.ROOT,
                icon: 'solar:shop-2-bold-duotone',
                labelKey: 'layout.navigation.branches',
                // Prefix match, like the Accounting shells: the Shifts tab
                // lives under this path and is the same nav destination, so it
                // should highlight Branches and title the navbar "Branches".
                matchPaths: [PATH.BRANCHES.ROOT],
                slug: 'branches',
            },
            {
                href: PATH.PRODUCTS.ROOT,
                icon: 'solar:box-bold-duotone',
                labelKey: 'layout.navigation.products',
                matchPaths: [PATH.PRODUCTS.ROOT],
                slug: 'products',
            },
            {
                href: PATH.CATEGORIES,
                icon: 'solar:widget-2-bold-duotone',
                labelKey: 'layout.navigation.categories',
                matchPaths: [PATH.CATEGORIES],
                slug: 'categories',
            },
            {
                href: PATH.ATTRIBUTES,
                icon: 'solar:tag-bold-duotone',
                labelKey: 'layout.navigation.attributes',
                matchPaths: [PATH.ATTRIBUTES],
                slug: 'attributes',
            },
            {
                href: PATH.SUPPLIERS,
                icon: 'solar:delivery-bold-duotone',
                labelKey: 'layout.navigation.suppliers',
                matchPaths: [PATH.SUPPLIERS],
                slug: 'suppliers',
            },
            {
                href: PATH.BRANDS,
                icon: 'solar:bookmark-bold-duotone',
                labelKey: 'layout.navigation.brands',
                matchPaths: [PATH.BRANDS],
                slug: 'brands',
            },
        ],
    },
    {
        icon: 'solar:notebook-square-bold-duotone',
        labelKey: 'layout.navigation.groups.accounting',
        slug: 'accounting',
        items: [
            {
                href: PATH.ACCOUNTING.BOOKS.ROOT,
                icon: 'solar:book-2-bold-duotone',
                labelKey: 'layout.navigation.books',
                // Prefix match, unlike its Accounting siblings: the four tab
                // routes live *under* this path and are the same nav
                // destination, so all of them should highlight Books and title
                // the navbar "Books".
                matchPaths: [PATH.ACCOUNTING.BOOKS.ROOT],
                // Same gate as the Accounting group — a ledger surface.
                roles: ['OrgAdmin', 'BranchAdmin'],
                slug: 'books',
            },
            {
                href: PATH.ACCOUNTING.STATEMENTS.ROOT,
                icon: 'solar:chart-2-bold-duotone',
                labelKey: 'layout.navigation.statements',
                // Prefix match, like the other shells: the tab routes live
                // under this path and are the same nav destination.
                matchPaths: [PATH.ACCOUNTING.STATEMENTS.ROOT],
                roles: ['OrgAdmin', 'BranchAdmin'],
                slug: 'statements',
            },
            {
                href: PATH.ACCOUNTING.REGISTERS.ROOT,
                icon: 'solar:box-bold-duotone',
                labelKey: 'layout.navigation.registers',
                // Prefix match, like the other shells: the tab routes live
                // under this path and are the same nav destination.
                matchPaths: [PATH.ACCOUNTING.REGISTERS.ROOT],
                roles: ['OrgAdmin', 'BranchAdmin'],
                slug: 'registers',
            },
            {
                href: PATH.ACCOUNTING.OPERATIONS.ROOT,
                icon: 'solar:checklist-minimalistic-bold-duotone',
                labelKey: 'layout.navigation.operations',
                // Prefix match, like the other shells: the tab routes live
                // under this path and are the same nav destination.
                matchPaths: [PATH.ACCOUNTING.OPERATIONS.ROOT],
                roles: ['OrgAdmin', 'BranchAdmin'],
                // Between Registers and Configurations, where it belongs on
                // both sides: recording an operation is daily work like a
                // register, and the templates behind it are configuration.
                slug: 'operations',
            },
            {
                href: PATH.ACCOUNTING.CONFIGURATIONS.ROOT,
                icon: 'solar:settings-bold-duotone',
                labelKey: 'layout.navigation.configurations',
                // Prefix match, like Books: the tab routes live under this path
                // and are the same nav destination.
                matchPaths: [PATH.ACCOUNTING.CONFIGURATIONS.ROOT],
                roles: ['OrgAdmin', 'BranchAdmin'],
                // Below Books, as Ledger settings sits below the surfaces it
                // configures: this is the group a user visits least often.
                slug: 'configurations',
            },
        ],
    },
];

/**
 * Whether a nav item should be shown to the given caller role. Items without a
 * `roles` constraint are visible to everyone; a null role (no session) only
 * sees unconstrained items.
 */
export const isNavigationItemVisible = (
    item: SecuredNavigationItem,
    role: UserRole | null,
): boolean => {
    if (!item.roles) {
        return true;
    }
    return role !== null && item.roles.includes(role);
};

/**
 * How specifically this item claims `pathname`: the length of its longest
 * matching path, or `null` when it does not match at all.
 *
 * A nav item matches its own path and everything beneath it, so nested routes
 * can be claimed by more than one item. Ranking by matched length picks the
 * item that is actually about this route (BOS-170); `PATH.HOME` is exempt from
 * prefix matching because every path starts with `/`.
 *
 * The case it was written for was `/sales/shifts`, claimed by both Sales and
 * Shifts. That pair is gone — the register is a tab of Branches now (BOS-173),
 * and no two items overlap today — but the rule stands: Sales still claims both
 * `/` and `/sales`, and the next nested item recreates the pair.
 */
export const matchedPathLength = (
    item: SecuredNavigationItem,
    pathname: string,
): number | null => {
    const lengths = (item.matchPaths ?? [item.href])
        .filter(
            (matchPath) =>
                pathname === matchPath ||
                (matchPath !== PATH.HOME &&
                    pathname.startsWith(`${matchPath}/`)),
        )
        .map((matchPath) => matchPath.length);

    return lengths.length === 0 ? null : Math.max(...lengths);
};

export const isNavigationItemActive = (
    item: SecuredNavigationItem,
    pathname: string,
): boolean => matchedPathLength(item, pathname) !== null;

/**
 * The one nav item a route belongs to: the most specific match, or `undefined`
 * when the URL is not a page.
 *
 * Both the sidebar's highlight and the navbar's title read this, so they cannot
 * disagree — which they did (BOS-170): the Shifts page highlighted Shifts and
 * was titled "SALES", because the two decided independently and one of them
 * took the first match.
 */
export const findActiveNavigationItem = (
    pathname: string,
): SecuredNavigationItem | undefined => {
    let best: SecuredNavigationItem | undefined;
    let bestLength = -1;

    for (const item of ALL_NAVIGATION_ITEMS) {
        const length = matchedPathLength(item, pathname);
        if (length !== null && length > bestLength) {
            best = item;
            bestLength = length;
        }
    }

    return best;
};

const ALL_NAVIGATION_ITEMS: SecuredNavigationItem[] = [
    ...SECURED_NAVIGATION_ITEMS,
    ...SECURED_NAVIGATION_GROUPS.flatMap((group) => group.items),
];

type SubpageNavigation = {
    /** Route pattern (a `PATH` constant; may carry `:params`). */
    path: string;
    titleKey: string;
    /** When set, the navbar back button always shows on this page and goes
     *  here — a plain navigation, regardless of how the page was reached.
     *  Nothing is saved on the way out. */
    backTarget?: string;
};

/**
 * Standalone sub-pages that live under a nav item's path (e.g. manual import
 * under `/purchases`) get their own navbar title here, checked before the
 * prefix match so they read as their own page rather than the parent section.
 * Patterns are matched with react-router's `matchPath`, so parameterised
 * routes (the manual-import `:draftId` resume route) resolve too.
 */
const SUBPAGE_NAVIGATION: SubpageNavigation[] = [
    {
        path: PATH.PURCHASES.MANUAL_IMPORT,
        titleKey: 'layout.navigation.manualImport',
        backTarget: PATH.PURCHASES.ROOT,
    },
    {
        path: PATH.PURCHASES.MANUAL_IMPORT_EDIT,
        titleKey: 'layout.navigation.manualImport',
        backTarget: PATH.PURCHASES.ROOT,
    },
    {
        path: PATH.PURCHASES.FILE_IMPORT,
        titleKey: 'layout.navigation.fileImport',
    },
    // Outside the Branches tab shell; always back to the branch list, however
    // the page was reached. The splat covers every step under it.
    {
        path: `${PATH.BRANCHES.IMPORT}/*`,
        titleKey: 'layout.navigation.branchImport',
        backTarget: PATH.BRANCHES.ROOT,
    },
    {
        path: PATH.PRODUCTS.NEW,
        titleKey: 'layout.navigation.newProduct',
        backTarget: PATH.PRODUCTS.ROOT,
    },
    // After NEW: `/products/:productId` matches `/products/new` too, and the
    // first match wins. No backTarget: history back returns to wherever the
    // user came from, so the list comes back with its filters still in its URL.
    {
        path: PATH.PRODUCTS.DETAIL,
        titleKey: 'layout.navigation.productDetails',
    },
    // No entry for the version history: it is a drawer over the Templates tab
    // and over the editor, not a screen of its own, so there is nothing to give
    // a title or a back button to.
    {
        path: PATH.ACCOUNTING.OPERATIONS.CATEGORIES,
        titleKey: 'layout.navigation.operationCategories',
        backTarget: PATH.ACCOUNTING.OPERATIONS.TEMPLATES,
    },
    {
        path: PATH.ACCOUNTING.OPERATIONS.EDIT,
        titleKey: 'layout.navigation.editOperation',
        // Always back to the Templates tab, however the editor was reached —
        // a deep link from a support message lands here as often as a click
        // from the catalog does.
        backTarget: PATH.ACCOUNTING.OPERATIONS.TEMPLATES,
    },
];

const findSubpageNavigation = (
    pathname: string,
): SubpageNavigation | undefined =>
    SUBPAGE_NAVIGATION.find(
        (subpage) => matchPath(subpage.path, pathname) !== null,
    );

export const getNavigationTitle = (
    pathname: string,
    t: (key: string) => string,
): string => {
    const subpage = findSubpageNavigation(pathname);
    if (subpage) {
        return t(subpage.titleKey);
    }

    const matchedItem = findActiveNavigationItem(pathname);

    if (matchedItem) {
        return t(matchedItem.labelKey);
    }

    // Nothing matched, which for a signed-in user means the URL is not a page.
    // "Admin workspace" over a 404 reads as though the page loaded.
    return t('layout.navigation.notFound');
};

/**
 * Fixed back target for the current page, or null when the navbar back
 * button should fall back to its default history behaviour.
 */
export const getNavigationBackTarget = (pathname: string): string | null =>
    findSubpageNavigation(pathname)?.backTarget ?? null;
